import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { PANEL_DEFAULT_WIDTH } from '../lib/panelWidth';
import { cancelAnswer, fetchMessages } from '../lib/sessionsApi';
import { toChatMessages } from '../lib/serverMessages';
import { streamChatResponse } from '../lib/streamChat';
import type {
  ChatErrorCode,
  ChatStreamEvent,
  MessageStatus,
} from '../types/types.ts';

export type ChatMessage = {
  id: string;
  role: 'USER' | 'AGENT';
  text: string;
  status?: MessageStatus;
  errorCode?: ChatErrorCode;
};

type SendOptions = {
  getAccessToken: () => Promise<string | undefined>;
  standortId: string;
};

type LoadOptions = Pick<SendOptions, 'getAccessToken'>;

type ChatData = {
  sessionId: string | undefined;
  messages: ChatMessage[];
  answer: string;
  isStreaming: boolean;
  error: ChatErrorCode | undefined;
  abortController: AbortController | undefined;
  isPending: boolean;
  pendingCancel: Promise<unknown> | undefined;
  canCancel: boolean;
};

type ChatState = ChatData & {
  userId: string | undefined;
  setUser: (userId: string) => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  isHistoryOpen: boolean;
  openHistory: () => void;
  closeHistory: () => void;
  loadSession: (
    sessionId: string,
    options: LoadOptions,
  ) => Promise<ChatErrorCode | undefined>;
  syncSession: (options: LoadOptions) => Promise<void>;
  sendQuestion: (question: string, options: SendOptions) => Promise<void>;
  cancel: (options: LoadOptions) => void;
  reset: () => void;
};

const initialChat: ChatData = {
  sessionId: undefined,
  messages: [],
  answer: '',
  isStreaming: false,
  error: undefined,
  abortController: undefined,
  isPending: false,
  pendingCancel: undefined,
  canCancel: false,
};

const createMessage = (
  role: ChatMessage['role'],
  text: string,
  status?: MessageStatus,
): ChatMessage => ({ id: crypto.randomUUID(), role, text, status });

// Beim Abbruch wird immer die Nachricht übernommen, auch ohne Text
function finishAnswer(
  state: ChatState,
  status?: MessageStatus,
): Partial<ChatState> {
  const messages =
    state.answer || status === 'CANCELLED'
      ? [...state.messages, createMessage('AGENT', state.answer, status)]
      : state.messages;
  return {
    messages,
    answer: '',
    isStreaming: false,
    isPending: false,
    abortController: undefined,
    canCancel: false,
  };
}

// Verarbeitet ein einzelnes Stream-Event und liefert den daraus resultierenden State
function applyEvent(
  state: ChatState,
  event: ChatStreamEvent,
): Partial<ChatState> {
  switch (event.type) {
    case 'session':
      return { sessionId: event.sessionId, canCancel: true };
    case 'chunk':
      return { answer: state.answer + event.chunk };
    case 'error':
      if (event.error.code === 'TURN_IN_PROGRESS') {
        // Die Frage wird nicht angenommen, weil schon eine Antwort läuft:
        // Frage aus dem Verlauf entfernen und auf die laufende Antwort warten
        return {
          messages: state.messages.slice(0, -1),
          answer: '',
          error: 'TURN_IN_PROGRESS',
          abortController: undefined,
          isPending: true,
          isStreaming: false,
          canCancel: false,
        };
      }
      return {
        ...finishAnswer(state),
        error: event.error.code,
        // Unbekannte oder fremde Session: die nächste Frage startet einen neuen Chat
        ...(event.error.code === 'SESSION_NOT_FOUND' && {
          sessionId: undefined,
        }),
      };
    case 'done':
      return finishAnswer(state);
  }
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      ...initialChat,
      userId: undefined as string | undefined,
      // Gehört der gespeicherte Chat einem anderen User, wird er verworfen
      setUser: (userId) => {
        if (get().userId === userId) {
          return;
        }
        get().abortController?.abort();
        set({ ...initialChat, isOpen: false, isHistoryOpen: false, userId });
      },
      isOpen: false,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false, isHistoryOpen: false }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      panelWidth: PANEL_DEFAULT_WIDTH,
      setPanelWidth: (panelWidth) => set({ panelWidth }),
      isHistoryOpen: false,
      openHistory: () => set({ isHistoryOpen: true }),
      closeHistory: () => set({ isHistoryOpen: false }),
      loadSession: async (sessionId, { getAccessToken }) => {
        const result = await fetchMessages(sessionId, { getAccessToken });
        if ('error' in result) {
          return result.error;
        }
        // Erst nach erfolgreichem Laden einen laufenden Stream abbrechen
        get().abortController?.abort();
        const chatMessages = toChatMessages(result.data);
        set({
          ...initialChat,
          sessionId,
          messages: chatMessages.messages,
          isHistoryOpen: false,
          isPending: chatMessages.isPending,
        });
        return undefined;
      },
      syncSession: async ({ getAccessToken }) => {
        const sessionId = get().sessionId;
        const userId = get().userId;
        const isStreaming = get().isStreaming;

        if (!sessionId) {
          return;
        }

        if (isStreaming) {
          return;
        }

        const result = await fetchMessages(sessionId, { getAccessToken });

        if (
          get().userId !== userId ||
          get().sessionId !== sessionId ||
          get().isStreaming !== isStreaming
        ) {
          return;
        }

        if ('error' in result) {
          if (result.error === 'SESSION_NOT_FOUND') {
            set(initialChat);
          }
          return;
        }

        const chatMessages = toChatMessages(result.data);
        if (!chatMessages.isPending) {
          set({ error: undefined });
        }

        set(chatMessages);
      },
      sendQuestion: async (question, { getAccessToken, standortId }) => {
        if (get().isStreaming || get().isPending) {
          return;
        }

        await get().pendingCancel;

        const abortController = new AbortController();
        set((state) => ({
          messages: [...state.messages, createMessage('USER', question)],
          answer: '',
          isStreaming: true,
          error: undefined,
          abortController,
          pendingCancel: undefined,
          canCancel: false,
        }));
        await streamChatResponse({
          request: { question, sessionId: get().sessionId, standortId },
          getAccessToken,
          signal: abortController.signal,
          onEvent: (event) => {
            // Events eines abgebrochenen oder zurückgesetzten Streams ignorieren
            if (get().abortController === abortController) {
              set((state) => applyEvent(state, event));
            }
          },
        });
      },
      // Bricht ab und sagt dem Server, was der User bis hierhin gesehen hat.
      cancel: ({ getAccessToken }) => {
        const { sessionId, answer, abortController, canCancel } = get();
        if (!canCancel || !sessionId || !abortController) {
          return;
        }

        set(finishAnswer(get(), 'CANCELLED'));
        set({
          pendingCancel: cancelAnswer(sessionId, answer, {
            getAccessToken,
          }).finally(() => abortController.abort()),
        });
      },
      // Bricht einen laufenden Stream ab und setzt den gesamten Chat-Zustand zurück
      reset: () => {
        get().abortController?.abort();
        set(initialChat);
      },
    }),
    {
      name: 'e2n-ai-chat',
      storage: createJSONStorage(() => sessionStorage),
      // Speichert den Zustand in die Browser-Session.
      partialize: (state) => ({
        userId: state.userId,
        isOpen: state.isOpen,
        panelWidth: state.panelWidth,
        sessionId: state.sessionId,
        messages: state.messages,
      }),
    },
  ),
);
